import { ArrowRight, ArrowUp, Envelope } from "react-bootstrap-icons";
import { Link, useNavigate } from "react-router-dom";
import { useLocale } from "../../contexts/LocaleContext";
import { HomeSection, navigateToHomeSection } from "../../utils/homeSectionNavigation";
import styles from "./Footer.module.scss";

type FooterLink =
  | { label: string; type: "route"; to: string }
  | { label: string; type: "home-section"; section: HomeSection }
  | { label: string; type: "external"; href: string };

type FooterSection = {
  title: string;
  links: FooterLink[];
};

export default function Footer() {
  const nav = useNavigate();
  const { messages } = useLocale();
  const year = new Date().getFullYear();
  const footerSections: FooterSection[] = [
    {
      title: messages.footer.companyTitle,
      links: [
        { label: messages.footer.aboutLink, type: "route", to: "/about" },
        { label: messages.footer.careerLink, type: "route", to: "/career" },
      ],
    },
    {
      title: messages.footer.servicesTitle,
      links: [{ label: messages.footer.developmentLink, type: "home-section", section: "services" }],
    },
    {
      title: messages.footer.documentsTitle,
      links: [
        { label: messages.footer.privacyLink, type: "route", to: "/privacy-policy" },
        { label: messages.footer.cookiesLink, type: "route", to: "/cookies-policy" },
        { label: messages.footer.personalDataLink, type: "route", to: "/personal-data-policy" },
      ],
    },
    {
      title: messages.footer.contactsTitle,
      links: [{ label: "info@catlogic.tech", type: "external", href: "mailto:info@catlogic.tech?subject=CatLogic%20inquiry" }],
    },
  ];

  return (
    <footer id="Footer" className={styles.footer}>
      <div className={styles.inner}>
        <div className={styles.top}>
          <div className={styles.brandCard}>
            <div className={styles.logoRow}>
              <div className={styles.logoMark} />
              <div>
                <strong>CatLogic</strong>
                <span>{messages.footer.brandRole}</span>
              </div>
            </div>

            <p>{messages.footer.brandBody}</p>

            <button type="button" className={styles.ctaButton} onClick={() => navigateToHomeSection(nav, "contacts")}>
              {messages.footer.ctaButton} <ArrowRight />
            </button>
          </div>

          <div className={styles.linksGrid}>
            {footerSections.map((section) => (
              <div key={section.title} className={styles.linkSection}>
                <h6>{section.title}</h6>

                {section.links.map((link) => {
                  if (link.type === "route") {
                    return (
                      <Link key={link.label} to={link.to} className={styles.linkItem}>
                        {link.label}
                      </Link>
                    );
                  }

                  if (link.type === "home-section") {
                    return (
                      <button
                        key={link.label}
                        type="button"
                        className={styles.linkButton}
                        onClick={() => navigateToHomeSection(nav, link.section)}
                      >
                        {link.label}
                      </button>
                    );
                  }

                  return (
                    <a key={link.label} className={`${styles.linkItem} ${styles.emailLink}`} href={link.href}>
                      <Envelope /> {link.label}
                    </a>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        <div className={styles.bottom}>
          <span>{`© ${year} CatLogic`}</span>
          <span>{messages.footer.bottomTagline}</span>
          <button
            type="button"
            className={styles.toTop}
            aria-label="Back to top"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            <ArrowUp />
          </button>
        </div>
      </div>
    </footer>
  );
}
